import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ACHIEVEMENTS } from "@/data/site";

function Counter({ to, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="font-display text-6xl md:text-7xl lg:text-8xl font-medium tracking-tighter text-brand-charcoal">
      {n}
      <span className="text-brand-brown">{suffix}</span>
    </span>
  );
}

export default function Achievements() {
  return (
    <section data-testid="achievements-section" className="relative py-24 md:py-40 border-t border-brand-border bg-brand-beige">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <p className="eyebrow mb-6">(06) — By the numbers</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium max-w-[16ch]">
              Numbers we&apos;re <span className="italic text-brand-brown">quietly proud of</span>.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6">
          {ACHIEVEMENTS.map((a, i) => (
            <motion.div
              key={a.label}
              data-testid={`achievement-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="border-t border-brand-brown/20 pt-6"
            >
              <Counter to={a.value} suffix={a.suffix} />
              <p className="mt-3 text-xs uppercase tracking-[0.24em] text-brand-charcoal/70 leading-relaxed max-w-[16ch]">
                {a.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
